import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import { allMainColors, DoughnutChart } from "@foodpilot/foods";
import i18next, { t } from "i18next";

import { formatNumber } from "src/utils/formatting";
import ActionPlanEmptyState from "./ActionPlanEmptyState";
import { ScoreChartValues } from "src/components/ScoreChart/ScoreChart";
import { CampaignSelector } from "src/components/Selector";

type ScoreSectionProps = {
    score: number;
    completionPercentage?: number;
    enabled?: boolean;
    chartData?: ScoreChartValues;
    unit?: string;
    precision?: number;
    ctaURL?: string;
    isLoading?: boolean;
};

export const ScoreSection = (props: ScoreSectionProps) => {
    const { score, completionPercentage = 0, enabled = true, chartData, unit, precision, ctaURL, isLoading } = props;
    const theme = useTheme();

    const chartColors = Object.values(allMainColors).slice(0, chartData?.values.length || 0);
    return (
        <Stack
            spacing={theme.spacing(1)}
            alignItems="center"
            p={theme.spacing(4)}
            sx={{
                height: {
                    xs: undefined,
                    lg: "100%",
                },
            }}
        >
            <Typography variant="h2" align="center">
                {t("puck.widgets.actionPlan.diagnostic.title")}
            </Typography>
            <CampaignSelector disableText textual />
            {enabled ?
                <>
                    <Box
                        position="relative"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        flexDirection="column"
                        sx={{
                            "& > .MuiStack-root": {
                                gap: theme.spacing(1.5),
                            },
                        }}
                    >
                        <DoughnutChart
                            values={chartData?.values || []}
                            colors={chartColors}
                            labels={chartData?.labels || []}
                            displayTotal={formatNumber(score, i18next.language, { maximumFractionDigits: precision })}
                            valuesUnit={unit}
                            spacing={5}
                            borderWidth={2}
                            isLoading={isLoading}
                            borderColor={theme.palette.common.white}
                            legend={undefined}
                            valuesFormatterFn={(value) =>
                                formatNumber(value, i18next.language, { maximumFractionDigits: precision })
                            }
                        />
                    </Box>
                    <Stack
                        gap={theme.spacing(1)}
                        direction="row"
                        alignItems="center"
                        justifyContent="center"
                        width="100%"
                        display="flex"
                        sx={{ paddingBottom: theme.spacing(1.5) }}
                    >
                        <Typography
                            variant="body2"
                            fontWeight={theme.typography.fontWeightMedium}
                            color="text.secondary"
                        >
                            {t("puck.widgets.actionPlan.diagnostic.questionnaireProgress")}
                        </Typography>
                        <Typography
                            variant="body-medium"
                            sx={{
                                backgroundColor: theme.palette.grey[500],
                                padding: theme.spacing(0.25, 1),
                                borderRadius: theme.spacing(0.5),
                                marginLeft: theme.spacing(1),
                            }}
                        >
                            {formatNumber(completionPercentage / 100, i18next.language, {
                                maximumFractionDigits: 0,
                                style: "percent",
                            })}
                        </Typography>
                    </Stack>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button variant="ternary" fullWidth size="small" href={ctaURL} disabled={!ctaURL}>
                        <Typography variant="body2" fontWeight={theme.typography.fontWeightBold}>
                            {t("puck.widgets.actionPlan.diagnostic.CTA.edit")}
                        </Typography>
                    </Button>
                </>
            :   <ActionPlanEmptyState
                    icon="diagnostic"
                    title={t("puck.widgets.actionPlan.diagnostic.noData")}
                    disabled={!ctaURL}
                    cta={{
                        url: ctaURL,
                        label: t("puck.widgets.actionPlan.diagnostic.CTA.create"),
                    }}
                />
            }
        </Stack>
    );
};

export default ScoreSection;
